/**
 * OPFSAdapter - Storage para archivos binarios grandes
 *
 * Usa Origin Private File System (OPFS) API.
 * Ideal para: música (7MB+), archivos grandes
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/File_System_API/Origin_private_file_system
 */

import { BaseAdapter } from './BaseAdapter';

export class OPFSAdapter extends BaseAdapter {
  constructor(directory = 'audio') {
    super('OPFSAdapter');
    this.directoryName = directory;
    this.rootHandle = null;
    this.dirHandle = null;
  }

  /**
   * Inicializa acceso a OPFS
   */
  async init() {
    if (this.initialized) return;

    try {
      // Verificar soporte de OPFS
      if (!navigator.storage?.getDirectory) {
        throw new Error('OPFS not supported in this browser');
      }

      // Obtener raíz de OPFS
      this.rootHandle = await navigator.storage.getDirectory();

      // Crear/obtener directorio para audio
      this.dirHandle = await this.rootHandle.getDirectoryHandle(this.directoryName, {
        create: true,
      });

      this.initialized = true;
    } catch (error) {
      console.error(`[OPFSAdapter] Failed to init:`, error);
      // OPFS puede no estar disponible en algunos browsers/contexts
      // Marcamos como initialized pero con dirHandle null
      this.initialized = true;
      this.dirHandle = null;
    }
  }

  /**
   * Verifica si OPFS está disponible
   */
  isAvailable() {
    return this.dirHandle !== null;
  }

  /**
   * Verifica si existe un archivo
   */
  async has(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return false;

    try {
      await this.dirHandle.getFileHandle(this._toFileName(key));
      return true;
    } catch (error) {
      // NotFoundError significa que no existe
      if (error.name === 'NotFoundError') {
        return false;
      }
      console.warn(`[OPFSAdapter] has() error:`, error);
      return false;
    }
  }

  /**
   * Obtiene un archivo como Blob
   */
  async get(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return null;

    try {
      const fileHandle = await this.dirHandle.getFileHandle(this._toFileName(key));
      const file = await fileHandle.getFile();
      return file; // File extiende Blob
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.warn(`[OPFSAdapter] get() error:`, error);
      return null;
    }
  }

  /**
   * Obtiene un archivo como Blob URL (para usar en audio/img src)
   */
  async getBlobUrl(key) {
    const blob = await this.get(key);
    if (!blob) return null;
    return URL.createObjectURL(blob);
  }

  /**
   * Guarda un Blob como archivo
   * @param {string} key
   * @param {Blob} blob
   * @param {Object} options - { version }
   */
  async set(key, blob, options = {}) {
    this.ensureInitialized();
    if (!this.isAvailable()) {
      console.warn(`[OPFSAdapter] OPFS not available, cannot save ${key}`);
      return;
    }

    try {
      const fileName = this._toFileName(key);
      const fileHandle = await this.dirHandle.getFileHandle(fileName, { create: true });

      // Escribir blob al archivo
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();

      // Guardar metadata si hay versión
      if (options.version) {
        await this._saveMetadata(key, options);
      }

      console.debug(`[OPFSAdapter] Saved: ${fileName} (${(blob.size / 1024 / 1024).toFixed(2)}MB)`);
    } catch (error) {
      console.error(`[OPFSAdapter] set() error:`, error);
      throw error;
    }
  }

  /**
   * Elimina un archivo
   */
  async delete(key) {
    this.ensureInitialized();
    if (!this.isAvailable()) return;

    try {
      await this.dirHandle.removeEntry(this._toFileName(key));
      await this._deleteMetadata(key);
    } catch (error) {
      if (error.name !== 'NotFoundError') {
        console.warn(`[OPFSAdapter] delete() error:`, error);
      }
    }
  }

  /**
   * Limpia todo el directorio
   */
  async clear() {
    this.ensureInitialized();
    if (!this.isAvailable()) return;

    try {
      // Eliminar directorio y recrear
      await this.rootHandle.removeEntry(this.directoryName, { recursive: true });
      this.dirHandle = await this.rootHandle.getDirectoryHandle(this.directoryName, {
        create: true,
      });
    } catch (error) {
      console.warn(`[OPFSAdapter] clear() error:`, error);
    }
  }

  /**
   * Obtiene lista de archivos
   */
  async keys() {
    this.ensureInitialized();
    if (!this.isAvailable()) return [];

    try {
      const keys = [];
      for await (const [name, handle] of this.dirHandle.entries()) {
        if (handle.kind === 'file' && !name.endsWith('.meta')) {
          keys.push(this._fromFileName(name));
        }
      }
      return keys;
    } catch (error) {
      console.warn(`[OPFSAdapter] keys() error:`, error);
      return [];
    }
  }

  /**
   * Obtiene estadísticas
   */
  async getStats() {
    const allKeys = await this.keys();
    let totalSize = 0;

    for (const key of allKeys) {
      const blob = await this.get(key);
      if (blob) {
        totalSize += blob.size;
      }
    }

    return {
      count: allKeys.length,
      size: totalSize,
      sizeFormatted: `${(totalSize / 1024 / 1024).toFixed(2)}MB`,
    };
  }

  /**
   * Valida si un archivo tiene la versión correcta
   */
  async validateVersion(key, currentVersion) {
    const meta = await this._getMetadata(key);

    if (!meta) {
      // Sin metadata, verificar si archivo existe
      const exists = await this.has(key);
      return { valid: exists, noMeta: true };
    }

    if (meta.version !== currentVersion) {
      return { valid: false, outdated: true };
    }

    return { valid: true };
  }

  // ==================== Helpers ====================

  /**
   * Convierte key a nombre de archivo seguro
   */
  _toFileName(key) {
    // Reemplazar caracteres no válidos
    return key.replace(/[^a-zA-Z0-9._-]/g, '_');
  }

  /**
   * Convierte nombre de archivo a key
   */
  _fromFileName(fileName) {
    // Por ahora, asumimos que el fileName es el key
    return fileName;
  }

  /**
   * Guarda metadata en archivo separado
   */
  async _saveMetadata(key, options) {
    try {
      const metaFileName = `${this._toFileName(key)}.meta`;
      const metaHandle = await this.dirHandle.getFileHandle(metaFileName, { create: true });
      const writable = await metaHandle.createWritable();
      await writable.write(JSON.stringify({ ...options, savedAt: Date.now() }));
      await writable.close();
    } catch (error) {
      console.warn(`[OPFSAdapter] Failed to save metadata:`, error);
    }
  }

  /**
   * Obtiene metadata
   */
  async _getMetadata(key) {
    try {
      const metaFileName = `${this._toFileName(key)}.meta`;
      const metaHandle = await this.dirHandle.getFileHandle(metaFileName);
      const file = await metaHandle.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  /**
   * Elimina metadata
   */
  async _deleteMetadata(key) {
    try {
      const metaFileName = `${this._toFileName(key)}.meta`;
      await this.dirHandle.removeEntry(metaFileName);
    } catch (error) {
      // Ignorar si no existe
    }
  }
}

export default OPFSAdapter;
